import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, ApiGatewayApi, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { MonitoringService } from '../../core/services/monitoring.service';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'apis' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'count', label: 'Reqs'    },
  { key: 'e5xx',  label: '5XX'     },
  { key: 'e4xx',  label: '4XX'     },
  { key: 'lat',   label: 'Latency' },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['endpointType', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'endpointType',
    label: 'Endpoint',
    options: [
      { value: 'EDGE',       label: 'Edge'      },
      { value: 'REGIONAL',   label: 'Regional'  },
      { value: 'PRIVATE',    label: 'Private'   },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * API Gateway monitoring is at the REST API level (CloudWatch ApiName
 * dimension). The monitored resource identifier is the API *name*, not the
 * REST API id — per-stage / per-route granularity is not supported in v1.
 */
@Component({
  selector: 'app-api-gateway-detail',
  templateUrl: './api-gateway-detail.component.html',
})
export class ApiGatewayDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  apis: ApiGatewayApi[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'apis',     label: 'REST APIs' },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'api-gateway', name: 'API Gateway', icon: 'globe', route: '/cloud/api-gateway', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('api-gateway').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the API list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getApiGateways(bypass).subscribe(apis => {
      this.apis       = apis;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'API_GATEWAY'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'apis';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts ─────────────────────────────────────────────────────────

  get totalCount():     number { return this.apis.length; }
  get monitoredCount(): number { return this.apis.filter(a => this.isMonitored(a.name)).length; }
  get edgeCount():      number { return this.apis.filter(a => a.endpointType === 'EDGE').length; }
  get regionalCount():  number { return this.apis.filter(a => a.endpointType === 'REGIONAL').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredApis(): ApiGatewayApi[] {
    let result = this.apis;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q) ||
      (a.description ?? '').toLowerCase().includes(q),
    );

    const endpoints = this.filters.facets['endpointType'] ?? [];
    if (endpoints.length) result = result.filter(a => endpoints.includes(a.endpointType));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(a => {
        const mon = this.isMonitored(a.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(apis: ApiGatewayApi[], sort: string): ApiGatewayApi[] {
    return [...apis].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(apiName: string): boolean { return this.cloud.isMonitored('api-gateway', apiName); }

  toggleMonitoring(apiName: string): void {
    const current = this.cloud.isMonitored('api-gateway', apiName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('api-gateway', apiName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(apiName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: apiName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(apiName: string): void  { this.drawerItemId = apiName; }
  closeItemSettings(): void                { this.drawerItemId = null; }

  itemOverrideSummary(apiName: string): string {
    return this.cloud.getItemOverrideSummary('api-gateway', apiName);
  }
}
